import { getDisplayValue } from '@features/filter/components/Filters/helpers/getDisplayValue';
import { getPriceValue } from '@features/filter/components/Filters/helpers/getPriceValue';
import { FilterPageContext } from '@features/filter/context';
import { Facet, type NumericHybridFacet, type SelectableFilters } from '@features/filter/types';
import { useContext } from 'react';

export function useSelectedFilters({ facets, separator }: { facets: Facet[]; separator: string }) {
  const { getFilter } = useContext(FilterPageContext);

  if (facets.length === 0) {
    return [];
  }

  function getCustomFilterValues(
    getFilter: SelectableFilters['getFilter'],
    facet: Facet
  ): string[] {
    const matchingFilter = getFilter?.(facet.name);

    if (matchingFilter?.values?.length) {
      const [matchingValue] = matchingFilter.values;
      const matchingFacet = facet.values.find(values => values.value === matchingValue);
      if (matchingFacet) {
        return [matchingFacet.displayName];
      }
      if (facet.type === 'range') {
        return [getPriceValue(matchingValue, separator)];
      }
      const displayValue = getDisplayValue(matchingValue);
      const unit = (facet as NumericHybridFacet)?.preferredUnit || '';
      return [`${displayValue} ${unit}`];
    }
    return [];
  }

  return facets
    .map(facet => {
      const selectedValues =
        facet.values?.filter(value => value.isSelected && value.count > 0) || [];

      if (selectedValues.length === 0) {
        const customValues = getCustomFilterValues(getFilter, facet);
        if (customValues.length > 0) {
          return {
            name: facet.name,
            values: customValues,
            selectedFilters: {
              displayName: facet.displayName,
              values: customValues
            }
          };
        }
        return undefined;
      }

      // for facets with filterType "combinedBooleanFilters" facet.name is undefined
      // and we need to use the selectedValues names instead
      // we join them with a comma
      // to then split and use them in FilterRemover.tsx
      const name = facet.name || selectedValues.map(value => value.name).join(',');

      return {
        name,
        values: selectedValues.map(value => value.value),
        selectedFilters: {
          displayName: facet.displayName,
          values: selectedValues.map(value => value.displayName)
        }
      };
    })
    .filter(filter => typeof filter !== 'undefined');
}
